import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { selectImportJob, selectImportProgress } from 'store/slices/importSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

interface ImportProgressProps {
  message?: string;
  onComplete?: () => void;
  onFailed?: (error?: string) => void;
}

const ImportProgress = ({ message = 'Importing your cards...', onComplete, onFailed }: ImportProgressProps) => {
  const job = useSelector((state: RootState) => selectImportJob(state));
  const { percentage, processedRows, totalRows } = useSelector(
    (state: RootState) => selectImportProgress(state)
  );

  // Track previous status to detect transitions
  const prevStatusRef = useRef(job.status);
  useEffect(() => {
    if (prevStatusRef.current !== job.status) {
      prevStatusRef.current = job.status;
      if (job.status === 'completed') {
        onComplete?.();
      } else if (job.status === 'failed') {
        onFailed?.(job.error || undefined);
      }
    }
  }, [job.status, job.error, onComplete, onFailed]);

  const hasProgress = percentage > 0;

  return (
    <div className="import-progress">
      <div className="import-progress-content">
        <FontAwesomeIcon icon={faSpinner} spin className="import-progress-spinner" />
        <h2>{message}</h2>
        {hasProgress ? (
          <p>
            Processing row {processedRows.toLocaleString()} of {totalRows.toLocaleString()} ({percentage}%)
          </p>
        ) : (
          <p className="import-progress-hint">This may take a moment for large collections.</p>
        )}
        {hasProgress && (
          <div className="import-progress-live-stats">
            <span style={{ color: '#4ade80' }}>{job.matched.toLocaleString()} matched</span>
            <span style={{ color: '#fbbf24' }}>{job.skipped.toLocaleString()} skipped</span>
            <span style={{ color: '#f87171' }}>{job.failed.toLocaleString()} failed</span>
          </div>
        )}
      </div>
      <div className="universal-import-progress-bar">
        <div
          className={`import-progress-bar-fill ${!hasProgress ? '' : ''}`}
          style={hasProgress ? { width: `${percentage}%`, animation: 'none' } : undefined}
        />
      </div>
    </div>
  );
};

export default ImportProgress;
